import styled, { css } from 'styled-components';

type ColumnProps = {
  $width?: string;
  $minWidth?: string;
  $flex?: number;
};

export const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 32px;
`;

export const Header = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

export const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export const Title = styled.h1`
  align-self: stretch;
  color: var(--Label-Normal, #171719);
  font-size: 28px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 700;
  line-height: 38.02px;
  word-wrap: break-word;
  margin: 0;
`;

export const FilterSection = styled.div`
  align-self: stretch;
  justify-content: space-between;
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const FilterControls = styled.div`
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  > div {
    justify-content: flex-start;
    align-items: center;
    display: flex;
  }

  > div > div {
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
    display: flex;
    flex-wrap: wrap;
  }
`;

export const Dropdown = styled.div`
  position: relative;
  justify-content: flex-start;
  align-items: center;
  display: flex;
`;

export const DropdownSelect = styled.select`
  padding: 7px 11px;
  padding-right: 35px;
  position: relative;
  border-radius: 10px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  background: white;
  justify-content: flex-start;
  align-items: center;
  display: flex;
  color: var(--Label-Strong, black);
  font-size: 15px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 500;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  word-wrap: break-word;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: var(--Label-Strong, black);
  }
`;

export const DropdownButton = styled.button`
  padding: 7px 11px;
  padding-right: 35px;
  position: relative;
  border-radius: 10px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  background: white;
  justify-content: flex-start;
  align-items: center;
  display: flex;
  color: var(--Label-Strong, black);
  font-size: 15px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 500;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  word-wrap: break-word;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: var(--Label-Strong, black);
  }
`;

export const DropdownIcon = styled.svg`
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--Label-Strong, black);
  pointer-events: none;
`;

export const SearchBox = styled.div`
  flex: 0 1 250px;
  min-width: 180px;
  height: 36px;
  padding: 8px;
  border-radius: 12px;
  outline: 1px solid var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  outline-offset: -1px;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  display: flex;
  background: white;

  > div {
    flex: 1;
    justify-content: flex-start;
    align-items: center;
    gap: 4px;
    display: flex;
  }

  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-width: 0;
  }
`;

export const SearchIcon = styled.div`
  height: 16px;
  width: 16px;
  padding-left: 2px;
  padding-right: 2px;
  justify-content: center;
  align-items: center;
  display: flex;
  opacity: 0.28;
`;

export const SearchInput = styled.input`
  flex: 1;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  border: none;
  outline: none;
  background: transparent;
  color: var(--Label-Normal, #171719);
  font-size: 12px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  word-wrap: break-word;

  &::placeholder {
    color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
    font-size: 12px;
    font-family: 'Pretendard Variable', sans-serif;
    font-weight: 400;
    line-height: 16.01px;
    letter-spacing: 0.30px;
    word-wrap: break-word;
  }
`;

export const TableContainer = styled.div`
  width: 100%;
  align-self: stretch;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid var(--Line-Solid-Normal, #E1E2E4);
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  background: white;
`;

export const TableInner = styled.div<{ $minWidth?: string }>`
  width: 100%;
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth};`}
`;

export const TableHeader = styled.div<{ $columns?: string; $gap?: string }>`
  width: fit-content;
  min-width: 100%;
  position: relative;
  background: var(--Fill-Alternative, rgba(112, 115, 124, 0.05));
  backdrop-filter: blur(32px);
  border-bottom: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  display: ${({ $columns }) => ($columns ? 'grid' : 'flex')};
  align-items: center;
  padding: 16px 20px;
  ${({ $columns, $gap }) =>
    $columns && `grid-template-columns: ${$columns}; column-gap: ${$gap || '16px'};`}
`;

export const HeaderCell = styled.div<ColumnProps>`
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 13px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0.25px;
  display: flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
  white-space: nowrap;
  ${({ $width }) => $width && `width: ${$width};`}
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth};`}
  ${({ $flex }) => ($flex ? `flex: ${$flex};` : 'flex-shrink: 0;')}
`;

export const TableBody = styled.div`
  width: 100%;
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
`;

export const TableRow = styled.div<{ $columns?: string; $gap?: string }>`
  width: fit-content;
  min-width: 100%;
  border-bottom: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  display: ${({ $columns }) => ($columns ? 'grid' : 'flex')};
  align-items: center;
  padding: 16px 20px;
  ${({ $columns, $gap }) =>
    $columns && `grid-template-columns: ${$columns}; column-gap: ${$gap || '16px'};`}

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: rgba(112, 115, 124, 0.03);
  }
`;

export const Cell = styled.div<ColumnProps>`
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  display: flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
  ${({ $width }) => $width && `width: ${$width};`}
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth};`}
  ${({ $flex }) => ($flex ? `flex: ${$flex};` : 'flex-shrink: 0;')}
`;

export const PostTitle = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  width: 100%;
`;

export const StatusBadge = styled.span<{ $answered: boolean }>`
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  white-space: nowrap;
  color: ${({ $answered }) => ($answered ? '#296768' : 'rgba(55, 56, 60, 0.61)')};
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: nowrap;
`;

export const DeleteButton = styled.button`
  padding: 7px 8px;
  border-radius: 8px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  background: white;
  cursor: pointer;
  font-size: 12px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 500;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  color: var(--Accent-Foreground-Red, #E52222);

  &:hover {
    background: rgba(112, 115, 124, 0.05);
  }
`;

export const HideButton = styled.button`
  padding: 7px 8px;
  border-radius: 8px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  background: white;
  cursor: pointer;
  font-size: 12px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 500;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  color: var(--Label-Normal, #171719);

  &:hover {
    background: rgba(112, 115, 124, 0.05);
  }
`;

export const PaginationContainer = styled.div`
  align-self: stretch;
  padding: 16px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

export const PaginationBtn = styled.button<{ $isCurrentPage?: boolean; $isNavBtn?: boolean }>`
  min-width: 20px;
  height: 30px;
  padding: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  margin: 0 8px;
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 15px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 400;
  line-height: 22.01px;
  letter-spacing: 0.14px;

  &:hover {
    background: rgba(112, 115, 124, 0.05);
  }

  ${({ $isCurrentPage }) =>
    $isCurrentPage &&
    css`
      background: rgba(23, 23, 25, 0.09);
      color: var(--Label-Strong, black);
      font-weight: 500;
    `}

  ${({ $isNavBtn }) =>
    $isNavBtn &&
    css`
      color: var(--Label-Disable, rgba(55, 56, 60, 0.16));

      &:disabled {
        color: var(--Label-Disable, rgba(55, 56, 60, 0.16));
        cursor: not-allowed;
      }
    `}
`;

export const PageJump = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
    font-size: 13px;
    font-family: 'Pretendard Variable', sans-serif;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: 0.25px;
  }
`;

export const PageInput = styled.input`
  width: 53px;
  padding: 4px 6px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  border-radius: 8px;
  background: white;
  outline: none;
  text-align: center;
  color: var(--Label-Normal, #171719);
  font-size: 14px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 500;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

export const NoData = styled.div`
  align-self: stretch;
  padding: 40px;
  text-align: center;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 400;
  line-height: 24px;
  word-wrap: break-word;
`;

export const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 16px;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-family: 'Pretendard Variable', sans-serif;
`;

export const ErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px;
  text-align: center;
  color: var(--Accent-Foreground-Red, #E52222);
  font-size: 16px;
  font-family: 'Pretendard Variable', sans-serif;
`;

export const RetryButton = styled.button`
  padding: 8px 16px;
  background: var(--Primary-Normal, #448181);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard Variable', sans-serif;
  cursor: pointer;

  &:hover {
    background: var(--Primary-Dark, #336666);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const DeleteModal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  padding: 32px 24px;
  text-align: center;
`;

export const DeleteIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: #ff9800;
`;

export const DeleteModalTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--Label-Normal, #171719);
  font-family: 'Pretendard Variable', sans-serif;
`;

export const DeleteWarning = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  line-height: 1.4;
  font-family: 'Pretendard Variable', sans-serif;
`;

export const DeleteForm = styled.div`
  margin-bottom: 24px;
  text-align: left;

  label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: var(--Label-Normal, #171719);
    margin-bottom: 8px;
    font-family: 'Pretendard Variable', sans-serif;
  }
`;

export const Optional = styled.span`
  font-weight: 400;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 12px;
`;

export const ReasonTextarea = styled.textarea`
  width: 100%;
  height: 80px;
  padding: 12px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  border-radius: 8px;
  font-size: 14px;
  color: var(--Label-Normal, #171719);
  resize: vertical;
  font-family: 'Pretendard Variable', sans-serif;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: var(--Primary-Normal, #448181);
  }

  &::placeholder {
    color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
  }
`;

export const DeleteActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

export const CancelButton = styled.button`
  flex: 1 1 0;
  padding-left: 28px;
  padding-right: 28px;
  padding-top: 12px;
  padding-bottom: 12px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: inline-flex;
  font-size: 16px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: var(--Primary-Normal, #448181);
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));

  &:hover {
    background: rgba(68, 129, 129, 0.05);
  }

  &:hover:not(:disabled) {
    background: rgba(68, 129, 129, 0.05);
  }

  &:disabled {
    color: var(--Label-Disable, rgba(55, 56, 60, 0.16));
    border-color: var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
    cursor: not-allowed;
  }
`;

export const ConfirmButton = styled.button`
  flex: 1 1 0;
  padding-left: 28px;
  padding-right: 28px;
  padding-top: 12px;
  padding-bottom: 12px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: inline-flex;
  font-size: 16px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--Primary-Normal, #448181);
  color: white;
  border: none;

  &:hover {
    background: #3a6b6b;
  }

  &:hover:not(:disabled) {
    background: #3a6b6b;
  }

  &:disabled {
    background: var(--Label-Disable, rgba(55, 56, 60, 0.16));
    cursor: not-allowed;
  }
`;

export const DateModal = styled.div`
  width: 100%;
  padding-bottom: 24px;
  background: var(--Background-Elevated-Normal, white);
  overflow: hidden;
  border-radius: 12px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: inline-flex;
  max-width: 500px;
  max-height: 90vh;
`;

export const ModalContent = styled.div`
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
`;

export const ModalHeader = styled.div`
  align-self: stretch;
  padding-top: 32px;
  padding-bottom: 16px;
  padding-left: 24px;
  padding-right: 24px;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  display: flex;
`;

export const ModalTitle = styled.h3`
  color: var(--Label-Strong, black);
  font-size: 20px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 28px;
  word-wrap: break-word;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--Label-Normal, #171719);
  }
`;

export const DateInputGroup = styled.div`
  width: 100%;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  display: flex;
  padding: 0 24px;
`;

export const DateLabel = styled.label`
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  display: inline-flex;
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
`;

export const DateInput = styled.input`
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  display: inline-flex;
  flex: 1 1 0;
  padding: 12px;
  position: relative;
  border-radius: 12px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  background: rgba(0, 0, 0, 0);
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  justify-content: center;
  align-items: center;
  gap: 12px;
  display: flex;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  outline: none;

  &:focus {
    border-color: var(--Primary-Normal, #448181);
  }

  &::placeholder {
    color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
    font-size: 16px;
    font-family: 'Pretendard Variable', sans-serif;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.09px;
    word-wrap: break-word;
  }
`;

export const PresetButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  padding: 0 24px;
`;

export const PresetButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  border-radius: 6px;
  background: transparent;
  color: var(--Label-Normal, #171719);
  font-size: 12px;
  font-family: 'Pretendard Variable', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0;

  &:hover {
    background: rgba(112, 115, 124, 0.05);
  }
`;

export const ModalActions = styled.div`
  padding: 24px;
  gap: 8px;
  align-self: stretch;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;

  > div {
    align-self: stretch;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    display: flex;
  }

  > div > div {
    align-self: stretch;
    padding: 24px;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 16px;
    display: flex;
  }

  > div > div > div {
    align-self: stretch;
    justify-content: center;
    align-items: center;
    gap: 12px;
    display: inline-flex;
  }
`;

export const AnswerModal = styled.div`
  width: 480px;
  height: 100vh;
  background: var(--Background-Normal-Normal, white);
  border-radius: 0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: hidden;
  position: fixed;
  top: 0;
  right: 0;
  z-index: 1001;
`;

export const AnswerModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const AnswerModalTitle = styled.h2`
  color: black;
  font-size: 24px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 700;
  line-height: 32.02px;
  word-wrap: break-word;
  margin: 0;
`;

export const AnswerModalContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(112, 115, 124, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(112, 115, 124, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(112, 115, 124, 0.5);
  }
`;

export const QuestionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const InfoRow = styled.div`
  display: flex;
  gap: 12px;
`;

export const InfoItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const InfoLabel = styled.label`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 15px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  word-wrap: break-word;
`;

export const InfoValue = styled.div`
  padding: 12px;
  background: var(--Fill-Normal, rgba(112, 115, 124, 0.08));
  border-radius: 12px;
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
  min-height: 52px;
  display: flex;
  align-items: center;
`;

export const QuestionContent = styled.div`
  padding: 12px;
  background: var(--Interaction-Disable, #F4F4F5);
  border-radius: 12px;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  min-height: 100px;
  max-height: 200px;
  overflow-y: auto;
`;

export const AnswerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
  max-height: 500px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(112, 115, 124, 0.1);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(112, 115, 124, 0.3);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(112, 115, 124, 0.5);
  }
`;

export const AnswerLabel = styled.label`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 15px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  word-wrap: break-word;
`;

export const AnswerEditor = styled.div`
  display: flex;
  flex-direction: column;

  > div {
    flex: 1;
    min-height: 0;
  }
`;

export const AnswerToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ToolbarLeft = styled.div`
  display: flex;
  gap: 16px;
`;

export const ToolbarButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(112, 115, 124, 0.1);
  }
`;

export const SubmitButton = styled.button`
  padding: 8px 16px;
  background: var(--Primary-Strong, #296768);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--Primary-Normal, #448181);
  }

  &:disabled {
    background: var(--Label-Disable, rgba(55, 56, 60, 0.16));
    cursor: not-allowed;
  }
`;

export const AnswerButton = styled.button`
  padding: 7px 8px;
  border-radius: 8px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  background: var(--Primary-Normal, #448181);
  color: white;
  cursor: pointer;
  font-size: 12px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 500;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--Primary-Dark, #336666);
  }
`;

export const EditButton = styled.button`
  padding: 7px 8px;
  border-radius: 8px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  background: var(--Accent-Foreground-Orange, #ff9800);
  color: white;
  cursor: pointer;
  font-size: 12px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 500;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #e68900;
  }
`;

export const MentorSelection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

export const MentorLabel = styled.label`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 15px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  word-wrap: break-word;
`;

export const MentorSelect = styled.select`
  padding: 12px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  border-radius: 12px;
  background: white;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: var(--Primary-Normal, #448181);
  }
`;

export const AttachmentList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const AttachmentLabel = styled.label`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 15px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 600;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  word-wrap: break-word;
`;

export const AttachmentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--Fill-Normal, rgba(112, 115, 124, 0.08));
  border-radius: 8px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
`;

export const AttachmentName = styled.span`
  color: var(--Label-Normal, #171719);
  font-size: 14px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 400;
  line-height: 20px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AttachmentRemove = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: var(--Accent-Foreground-Red, #E52222);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #c41e3a;
  }
`;
